import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getMystudioErrorMessage, useMystudioPublicContentV4 } from "../../Common/api/v4";
import { getAccountData } from "../../Common/ts/commonTools";
import { MobileBottomTabBar } from "../../components/MobileAppShell";
import { withMobileView } from "../../components/MobileAppShell/mobileNavigation";
import MystudioDataState from "../../components/MystudioDataState";
import "./MobileFaqPage.css";

type MobileFaqPageProps = {
    onBack: () => void;
};

type NewsMode = "all" | "notice" | "faq";
type MobileNewsView = "list" | "detail";

type NewsItem = {
    id: number | string;
    type: Exclude<NewsMode, "all">;
    title: string;
    date: string;
    content: string;
    office?: string;
};

function StatusBar() {
    return (
        <div className="mobile-news-status" aria-hidden="true">
            <span>10:20</span>
            <span className="mobile-news-status__icons"><i /><i /><i /></span>
        </div>
    );
}

function BackIcon() {
    return (
        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path d="m15 5-7 7 7 7" />
        </svg>
    );
}

function ChevronRight() {
    return (
        <svg viewBox="0 0 12 16" aria-hidden="true" focusable="false">
            <path d="m3 2 6 6-6 6" />
        </svg>
    );
}

function ChevronDown() {
    return (
        <svg viewBox="0 0 16 10" aria-hidden="true" focusable="false">
            <path d="m2 2 6 6 6-6" />
        </svg>
    );
}

function Header({ title, onBack }: { title: string; onBack: () => void }) {
    return (
        <header className="mobile-news-header" data-node-id="7123:20542">
            <button type="button" aria-label="뒤로가기" onClick={onBack}>
                <BackIcon />
            </button>
            <h1>{title}</h1>
        </header>
    );
}

function NewsTabs({ mode, onChange }: { mode: NewsMode; onChange: (mode: NewsMode) => void }) {
    return (
        <nav className="mobile-news-tabs" aria-label="소식센터 탭" data-node-id="7123:20477">
            <button type="button" className={mode === "all" ? "is-active" : ""} onClick={() => onChange("all")}>전체</button>
            <button type="button" className={mode === "notice" ? "is-active" : ""} onClick={() => onChange("notice")}>공지사항</button>
            <button type="button" className={mode === "faq" ? "is-active" : ""} onClick={() => onChange("faq")}>FAQ</button>
        </nav>
    );
}

function Summary({ mode, count }: { mode: NewsMode; count: number }) {
    const label = mode === "all" ? "전체" : mode === "notice" ? "공지사항" : "FAQ";

    return (
        <section className="mobile-news-summary" data-node-id="7123:20496">
            <div>
                <strong>{label}</strong>
                <span>{count}</span>
            </div>
            <button type="button">최신순 <ChevronDown /></button>
        </section>
    );
}

export default function MobileFaqPage({ onBack }: MobileFaqPageProps) {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const requestedTab = searchParams.get("tab");
    const initialMode: NewsMode = requestedTab === "notice" || requestedTab === "faq"
        ? requestedTab
        : "all";
    const [mode, setMode] = useState<NewsMode>(initialMode);
    const [view, setView] = useState<MobileNewsView>("list");
    const [selectedItem, setSelectedItem] = useState<NewsItem | null>(null);
    const [expandedFaqId, setExpandedFaqId] = useState<number | string | null>(null);
    const officeId = getAccountData.office_id();
    const publicContentScope = officeId > 0 ? { officeId } : {};
    const noticeQuery = useMystudioPublicContentV4({
        ...publicContentScope,
        type: "notice",
        limit: 50,
    });
    const faqQuery = useMystudioPublicContentV4({
        ...publicContentScope,
        type: "faq",
        limit: 50,
    });

    const faqItems: NewsItem[] = (faqQuery.data?.data?.items || []).map((item) => ({
        id: item.id,
        type: "faq",
        title: item.title,
        date: item.publishedAt ? String(item.publishedAt).slice(0, 10).replace(/-/g, ".") : "",
        content: item.content || item.summary || "",
        office: "FAQ",
    }));
    const noticeItems: NewsItem[] = (noticeQuery.data?.data?.items || []).map((item) => ({
        id: item.id,
        type: "notice",
        title: item.title,
        date: item.publishedAt ? String(item.publishedAt).slice(0, 10).replace(/-/g, ".") : "",
        content: item.content || item.summary || "",
        office: item.officeId ? `스튜디오 ${item.officeId}` : undefined,
    }));
    const activeItems: NewsItem[] = (
        mode === "notice"
            ? noticeItems
            : mode === "faq"
                ? faqItems
                : [...noticeItems, ...faqItems]
    ).sort((left, right) => {
        const dateOrder = right.date.localeCompare(left.date);
        return dateOrder || String(right.id).localeCompare(String(left.id));
    });
    const displayCount = activeItems.length;

    const handleModeChange = (nextMode: NewsMode) => {
        setMode(nextMode);
        setView("list");
        setSelectedItem(null);
        setExpandedFaqId(null);
    };

    const handleOpenItem = (item: NewsItem) => {
        if (mode === "faq") {
            setExpandedFaqId((prev) => (prev === item.id ? null : item.id));
            return;
        }

        setSelectedItem(item);
        setView("detail");
    };

    const handleBack = () => {
        if (view === "detail") {
            setView("list");
            setSelectedItem(null);
            return;
        }

        onBack();
    };

    const navigateMobile = (path: string) => navigate(withMobileView(path));

    const renderList = () => (
        <>
            <NewsTabs mode={mode} onChange={handleModeChange} />
            <Summary mode={mode} count={displayCount} />
            <div className="mobile-news-list" data-node-id="7123:20507">
                {(mode === "all" || mode === "faq") && !faqQuery.configured ? <MystudioDataState type="error" title="FAQ를 불러올 수 없어요" description="MyStudio API 연결 설정을 확인해 주세요." compact /> : null}
                {(mode === "all" || mode === "faq") && faqQuery.isLoading ? <MystudioDataState type="loading" title="FAQ를 불러오고 있어요" compact /> : null}
                {(mode === "all" || mode === "faq") && faqQuery.isError ? <MystudioDataState type="error" description={getMystudioErrorMessage(faqQuery.error)} onRetry={() => faqQuery.refetch()} compact /> : null}
                {(mode === "all" || mode === "notice") && !noticeQuery.configured ? <MystudioDataState type="error" title="공지사항을 불러올 수 없어요" description="MyStudio API 연결 설정을 확인해 주세요." compact /> : null}
                {(mode === "all" || mode === "notice") && noticeQuery.isLoading ? <MystudioDataState type="loading" title="공지사항을 불러오고 있어요" compact /> : null}
                {(mode === "all" || mode === "notice") && noticeQuery.isError ? <MystudioDataState type="error" description={getMystudioErrorMessage(noticeQuery.error)} onRetry={() => noticeQuery.refetch()} compact /> : null}
                {((mode === "faq" && faqQuery.isSuccess)
                    || (mode === "notice" && noticeQuery.isSuccess)
                    || (mode === "all" && faqQuery.isSuccess && noticeQuery.isSuccess))
                    && activeItems.length === 0
                    ? <p className="mobile-news-state">등록된 소식이 없습니다.</p>
                    : null}
                {activeItems.map((item) => {
                    const isFaqItem = item.type === "faq";
                    const isExpanded = expandedFaqId === item.id;
                    const answerId = `mobile-faq-answer-${String(item.id).replace(/[^a-zA-Z0-9_-]/g, "-")}`;

                    if (isFaqItem) {
                        return (
                            <article key={item.id} className={isExpanded ? "mobile-news-faq is-expanded" : "mobile-news-faq"}>
                                <button
                                    className="mobile-news-faq__question"
                                    type="button"
                                    data-node-id="7123:20508"
                                    onClick={() => handleOpenItem(item)}
                                    aria-expanded={isExpanded}
                                    aria-controls={answerId}
                                >
                                    <em>Q</em>
                                    <span>
                                        <strong>{item.title}</strong>
                                        <time>{item.date}</time>
                                    </span>
                                    <ChevronDown />
                                </button>
                                {isExpanded ? (
                                    <div id={answerId} className="mobile-news-faq__answer">
                                        <em>A</em>
                                        <p>{item.content}</p>
                                    </div>
                                ) : null}
                            </article>
                        );
                    }

                    return (
                        <button key={item.id} className="mobile-news-row" type="button" data-node-id="7123:20508" onClick={() => handleOpenItem(item)}>
                            <span>
                                <strong>{item.title}</strong>
                                <time>{item.date}</time>
                            </span>
                            <ChevronRight />
                        </button>
                    );
                })}
            </div>
        </>
    );

    const renderDetail = () => {
        const item = selectedItem;
        if (!item) return null;
        return (
            <article className="mobile-news-detail" data-node-id="7123:12174">
                <section className="mobile-news-detail-card">
                    <h2>{item.title}</h2>
                    <div className="mobile-news-detail-meta">
                        {item.office ? <span>{item.office}</span> : null}
                        {item.date ? <span>{item.date}</span> : null}
                        <i aria-hidden="true">•••</i>
                    </div>
                    <p>{item.content}</p>
                    {item.date ? <time>{item.date}</time> : null}
                </section>
                <button className="mobile-news-list-button" type="button" onClick={() => setView("list")}>목록</button>
            </article>
        );
    };

    return (
        <section className="mobile-news-page mobile-app-frame" data-node-id="7123:20474">
            <StatusBar />
            <Header title={view === "detail" ? (selectedItem?.type === "faq" ? "FAQ" : "공지사항") : "소식센터"} onBack={handleBack} />
            <main className={view === "detail" ? "mobile-news-scroll mobile-news-scroll--detail" : "mobile-news-scroll"}>
                {view === "detail" ? renderDetail() : renderList()}
            </main>
            <MobileBottomTabBar activePath="/tabs/home" onNavigate={navigateMobile} />
        </section>
    );
}
